/**
 * Presentation primitives.
 *
 * The visual language follows `ai-security-ops` per
 * [ADR-0017](../../../../docs/05-decisions/adr-0017-ux-reuse-from-ai-security-ops.md): the same
 * dark shell, the same card-and-stat vocabulary, the same habit of putting a plain-language hint
 * under every heading. What is *not* carried over is its implementation — that codebase uses
 * Tailwind utility classes, and Conduit renders five themes from semantic custom properties under
 * a Content Security Policy that forbids `unsafe-inline`.
 *
 * So every component here references semantic tokens (`--color-danger`), never palette values and
 * never a literal color. A component asking for "red" cannot be re-themed; one asking for "danger"
 * can.
 */

import type { JSX, ReactNode } from 'react';

import './ui.css';

/** A titled panel. The workhorse of every screen. */
export function Card({
  title,
  subtitle,
  right,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      {(title !== undefined || right !== undefined) && (
        <header className="card__head">
          <div>
            {title !== undefined && <h2 className="card__title">{title}</h2>}
            {subtitle !== undefined && <p className="card__subtitle">{subtitle}</p>}
          </div>
          {right !== undefined && <div className="card__right">{right}</div>}
        </header>
      )}
      <div className="card__body">{children}</div>
    </section>
  );
}

/**
 * A page heading with its purpose stated underneath.
 *
 * The subtitle is required rather than optional, deliberately. Every screen in this product
 * answers a specific question, and a heading that does not say which one leaves the reader to
 * infer it from the table below — which is how "findings" and "vulnerabilities" become
 * indistinguishable to everyone except the person who built the screen.
 */
export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle: string;
  right?: ReactNode;
}): JSX.Element {
  return (
    <header className="page-head">
      <div>
        <h1 className="page-head__title">{title}</h1>
        <p className="page-head__subtitle">{subtitle}</p>
      </div>
      {right !== undefined && <div className="page-head__right">{right}</div>}
    </header>
  );
}

/** A single number with its label. */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
  tone?: Tone | undefined;
}): JSX.Element {
  return (
    <div className="stat">
      <div className={`stat__value stat__value--${tone}`}>{value}</div>
      <div className="stat__label">{label}</div>
      {hint !== undefined && <div className="stat__hint">{hint}</div>}
    </div>
  );
}

/** A row of stats, as on the estate overview. */
export function StatRow({ children }: { children: ReactNode }): JSX.Element {
  return <div className="stat-row">{children}</div>;
}

/** Semantic emphasis. Never a color name — see the module note. */
export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'muted';

/** A small status chip. */
export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: Tone | undefined;
  title?: string | undefined;
}): JSX.Element {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      {children}
    </span>
  );
}

/** Maps a severity to a tone once, so no screen invents its own mapping. */
export function severityTone(severity: string): Tone {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warn';
    case 'medium':
      return 'info';
    case 'low':
    case 'info':
      return 'muted';
    default:
      return 'neutral';
  }
}

/** A simple data table. */
export function Table({
  columns,
  children,
  empty,
}: {
  columns: string[];
  children: ReactNode;
  empty?: string;
}): JSX.Element {
  const rows = Array.isArray(children) ? children : [children];
  const isEmpty = rows.flat().filter(Boolean).length === 0;

  if (isEmpty) {
    return <p className="table__empty">{empty ?? 'Nothing here yet.'}</p>;
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/** Shown while a request is in flight. */
export function Spinner({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return (
    <p className="spinner" role="status">
      {label}
    </p>
  );
}

/**
 * Shown when a request fails.
 *
 * Renders the server's message rather than a generic apology. The API returns messages written to
 * be actionable ("a before or after capture must name the ticket it brackets"), and replacing that
 * with "something went wrong" throws away the only useful part.
 */
export function ErrorBox({ error }: { error: unknown }): JSX.Element | null {
  if (error === null || error === undefined) return null;
  // An `unknown` that is not an Error could be anything, and `String(obj)` renders
  // "[object Object]" — which tells the reader nothing and hides the actual failure.
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'request failed';
  return (
    <div className="errorbox" role="alert">
      {message}
    </div>
  );
}

/**
 * An explanation rendered as evidence rather than prose.
 *
 * Used for the risk formula and the diff findings. Monospaced because these are formulas people
 * check term by term, and a proportional font makes `×1.6` hard to align across rows.
 */
export function Formula({ children }: { children: ReactNode }): JSX.Element {
  return <code className="formula">{children}</code>;
}

/** An empty-state note that says what would fill the screen. */
export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <p className="empty">{children}</p>;
}
